import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Building2, TrendingUp, Calendar, Package } from 'lucide-react';
import Logo from '../components/Logo';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-slate-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <Logo variant="full" size="h-12" />
          </div>
          <Button 
            onClick={() => navigate('/auth')}
            data-testid="header-auth-btn"
            className="bg-rose-600 hover:bg-rose-700 text-white shadow-sm transition-all rounded-lg px-6"
          >
            Connexion
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8 animate-fade-in">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Maximisez vos revenus avec
              <br />
              <span className="text-rose-600">les options premium</span>
            </h1>
            <p className="text-lg leading-relaxed text-slate-600 max-w-2xl mx-auto">
              La plateforme dédiée aux concierges Airbnb pour vendre des options supplémentaires à leurs voyageurs : champagne, fleurs, early check-in et plus encore.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/auth')}
                data-testid="hero-get-started-btn"
                className="bg-rose-600 hover:bg-rose-700 text-white shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 rounded-lg px-8 py-6 text-lg"
              >
                Commencer gratuitement
              </Button>
              <Button 
                variant="secondary"
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-learn-more-btn"
                className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 rounded-lg px-8 py-6 text-lg"
              >
                En savoir plus
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Tout ce dont vous avez besoin</h2>
            <p className="text-base leading-relaxed text-slate-600 max-w-2xl mx-auto">Une solution complète pour gérer et vendre vos options premium</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Package,
                title: 'Options personnalisées',
                description: 'Créez et gérez vos options pour chaque logement avec des prix sur mesure',
              },
              {
                icon: Calendar,
                title: 'Calendrier intégré',
                description: 'Visualisez toutes les options achetées par date et logement',
              },
              {
                icon: TrendingUp,
                title: 'Dashboard financier',
                description: 'Suivez vos revenus en temps réel par option et par logement',
              },
              {
                icon: Building2,
                title: 'Gestion multi-logements',
                description: 'Gérez tous vos logements et prestataires depuis une interface unique',
              },
            ].map((feature, index) => (
              <div key={index} className="bg-white border border-slate-100 shadow-sm rounded-xl p-6 hover-lift">
                <feature.icon className="w-10 h-10 text-rose-600 mb-4" strokeWidth={1.5} />
                <h3 className="text-xl font-medium text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{feature.title}</h3>
                <p className="text-sm leading-normal text-slate-500">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-rose-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-semibold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Prêt à augmenter vos revenus ?</h2>
          <p className="text-lg text-rose-100 mb-8">Rejoignez les concierges qui font confiance à Plus</p>
          <Button 
            onClick={() => navigate('/auth')}
            data-testid="cta-signup-btn"
            className="bg-white text-rose-600 hover:bg-slate-50 shadow-lg rounded-lg px-8 py-6 text-lg font-medium"
          >
            Créer mon compte
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm">© 2026 Plus. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
